import {
  Grid,
  Theme,
  Typography,
  createStyles,
  makeStyles,
} from '@material-ui/core';
import { colors, moneyFormat } from 'design-system';
import { uniqueId } from 'lodash';
import React from 'react';

import {
  limitForMaxValue,
  limitsHaveRange,
} from 'data-layer-cr/dist/functions/coverages';
import { coverageForCode } from 'data-layer-cr/dist/functions/quotations';
import {
  CoverageCatalog,
  QuotationCoverage,
  SpecialCoverageCatalog,
} from 'data-layer-cr/dist/models';
import { CoverageLimit } from 'data-layer-cr/dist/models/CoverageCatalog';
import PricingProfileResponse from '../../../../../models/responseTypes/PricingProfileResponse';

type Props = {
  hideNotFound?: boolean;
  coverages: CoverageCatalog[];
  specialCoverages: SpecialCoverageCatalog[];
  simplifiedCoverages: QuotationCoverage[];
  currentProfile: PricingProfileResponse;
};

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    content: {
      marginTop: theme.spacing(0),
    },
    cardValue: {
      display: 'inline-block',
      textAlign: 'center',
    },
    coverageName: {
      color: colors.abbey,
      fontWeight: 400,
      fontSize: '15px',
    },
    textSilver: {
      color: colors.silver,
    },
    coveragePrice: {
      fontWeight: 900,
      fontSize: '23px',
    },
  }),
);

const { formatMoney } = moneyFormat;

const CoverageDetailList = ({
  hideNotFound = false,
  coverages,
  specialCoverages,
  simplifiedCoverages,
  currentProfile,
}: Props): JSX.Element => {
  const classes = useStyles();

  function makeCoverageDetailRangeLimitRow(
    coverage: CoverageCatalog,
    limit: CoverageLimit,
  ): React.ReactNode {
    return (
      <Grid
        key={`range-row-${uniqueId()}`}
        item
        xs={12}
        container
        alignItems="center"
      >
        <Grid item xs={12} container justifyContent="flex-start">
          <Typography className={classes.coverageName} variant="body2">
            {coverage.name}
          </Typography>
        </Grid>
        <Grid
          item
          xs={12}
          container
          justifyContent="flex-start"
          alignItems="flex-start"
        >
          <div className={classes.cardValue}>
            <Typography variant="h2">
              {formatMoney(limit.minimum, false, currentProfile.typeOfMoney)}
            </Typography>
            <Typography variant="caption">Por persona</Typography>
          </div>
          <div className={classes.cardValue}>
            <Typography variant="h2">&nbsp;/&nbsp;</Typography>
          </div>
          <div className={classes.cardValue}>
            <Typography variant="h2">
              {formatMoney(limit.maximum, false, currentProfile.typeOfMoney)}
            </Typography>
            <Typography variant="caption">Por accidente</Typography>
          </div>
        </Grid>
      </Grid>
    );
  }

  function makeCoverageDetailSingleLimitRow(
    coverage: CoverageCatalog,
    value: number,
  ): React.ReactNode {
    return (
      <Grid
        key={`single-limit-row-${uniqueId()}`}
        item
        xs={12}
        container
        alignItems="center"
        style={{ padding: '10px 7px' }}
      >
        <Grid item xs={12} container justifyContent="flex-start">
          <Typography className={classes.coverageName} variant="body2">
            {coverage.name}
          </Typography>
        </Grid>
        <Grid item xs={12} container justifyContent="flex-start">
          <Typography variant="h2" className={classes.coveragePrice}>
            {formatMoney(value, false, currentProfile.typeOfMoney)}
          </Typography>
        </Grid>
      </Grid>
    );
  }

  function makeCoverageDetailWithoutLimitRow(
    coverage: CoverageCatalog,
  ): React.ReactNode {
    if (hideNotFound) {
      return <React.Fragment key={`hidden-${uniqueId()}`} />;
    }

    return (
      <Grid
        key={`no-limit-row-${uniqueId()}`}
        item
        xs={12}
        container
        alignItems="center"
      >
        <Grid item xs={12} container justifyContent="flex-start">
          <Typography className={classes.coverageName} variant="body2">
            {coverage.name}
          </Typography>
        </Grid>
        <Grid item xs={12} container justifyContent="flex-start">
          <Typography
            data-testid="not-applicable"
            className={classes.textSilver}
            variant="h2"
          >
            n/a
          </Typography>
        </Grid>
      </Grid>
    );
  }

  function makeRowForSpecialCoverage(
    specialCoverage: SpecialCoverageCatalog,
  ): React.ReactNode {
    return (
      <Grid
        data-testid="special-coverage"
        key={`special-row-${uniqueId()}`}
        item
        xs={12}
        container
        alignItems="center"
      >
        <Grid item xs={12} container justifyContent="flex-start">
          <Typography className={classes.coverageName} variant="body2">
            {specialCoverage.label}
          </Typography>
        </Grid>
        <Grid item xs={12} container justifyContent="flex-start">
          <Typography variant="h2">{specialCoverage.content}</Typography>
        </Grid>
      </Grid>
    );
  }

  /** Create display rows */
  const displayRows = coverages.map((coverage): React.ReactNode => {
    const simplifiedCoverage = coverageForCode(
      simplifiedCoverages,
      coverage.code,
    );
    if (!simplifiedCoverage) {
      return makeCoverageDetailWithoutLimitRow(coverage);
    }
    if (limitsHaveRange(coverage)) {
      const usedLimit = limitForMaxValue(
        coverage,
        simplifiedCoverage.insuredValue,
      );
      if (!usedLimit) {
        return makeCoverageDetailWithoutLimitRow(coverage);
      }
      return makeCoverageDetailRangeLimitRow(coverage, usedLimit);
    }
    return makeCoverageDetailSingleLimitRow(
      coverage,
      simplifiedCoverage.insuredValue,
    );
  });

  /** Inject additional "special" coverages */
  specialCoverages.forEach((specialCoverage): void => {
    displayRows.push(makeRowForSpecialCoverage(specialCoverage));
  });

  return (
    <Grid
      data-testid="content"
      container
      spacing={2}
      className={classes.content}
    >
      {displayRows}
    </Grid>
  );
};

export default CoverageDetailList;
